<template>
    <div class="global-search">
        <!-- Variant 1: Full search trigger with text and keyboard shortcut -->
        <!-- <button type="button" class="search-trigger" @click="openSearch">
            <e-icon icon="search" size="is-small" />
            <span class="trigger-text">Search anything...</span>
            <span class="search-shortcut">
                <kbd>{{ isMac ? '&#8984;' : 'Ctrl' }}</kbd>
                <kbd>K</kbd>
            </span>
        </button> -->

        <!-- Variant 2: Minimal icon-only search trigger -->
        <button type="button" class="navbar-icon-button search-trigger search-trigger--icon-only" @click="openSearch">
            <e-icon icon="pi pi-search" class="search-trigger-icon" />
        </button>

        <!-- Modal overlay and dialog -->
        <Teleport to="body">
            <Transition name="modal">
                <div v-if="isOpen" class="search-modal-overlay" @click.self="closeSearch">
                    <div class="search-modal">
                        <!-- Search input -->
                        <div class="search-input-wrapper">
                            <e-icon icon="search" size="is-small" class="search-icon" />
                            <input
                                ref="searchInput"
                                v-model="searchQuery"
                                type="text"
                                class="search-input"
                                placeholder="Start typing..."
                                @input="debouncedSearch"
                                @keydown.escape="closeSearch"
                                @keydown.down.prevent="navigateDown"
                                @keydown.up.prevent="navigateUp"
                                @keydown.enter.prevent="handleSearchEnter"
                            />
                            <button
                                v-show="searchQuery.length > 0"
                                type="button"
                                class="close-button"
                                aria-label="Clear search"
                                @click="clearSearch"
                            >
                                <e-icon icon="times" size="is-small" />
                            </button>
                        </div>

                        <div class="search-type-filters">
                            <div class="search-type-filters-list">
                                <button
                                    v-for="typeOption in availableTypes"
                                    :key="typeOption.key"
                                    type="button"
                                    class="type-filter-option"
                                    :class="{ 'is-selected': isTypeSelected(typeOption.key) }"
                                    :aria-pressed="isTypeSelected(typeOption.key)"
                                    :aria-label="`${typeOption.label}, ${formatTypeFilterCount(typeOption.count)} results`"
                                    @click="toggleTypeFilter(typeOption.key)"
                                >
                                    <span class="type-filter-option-icon">
                                        <e-icon :icon="typeOption.icon" size="is-small" />
                                    </span>
                                    <span class="type-filter-option-label">{{ typeOption.label }}</span>
                                    <span v-if="typeOption.count > 0" class="type-filter-option-count">{{
                                        formatTypeFilterCount(typeOption.count)
                                    }}</span>
                                </button>
                            </div>
                            <button
                                v-if="hasActiveFilters"
                                type="button"
                                class="search-type-filters-clear"
                                @click="clearTypeFilters"
                            >
                                Clear filters
                            </button>
                        </div>

                        <div v-if="showResultsAreaDivider" class="results-divider" />

                        <div
                            v-if="showSortToolbar"
                            class="global-search-sort-toolbar"
                        >
                            <div class="search-results-title-group">
                                <span v-if="!isRecentPreviewVisible" class="search-results-title">Results:</span>
                                <template v-else>
                                    <span class="search-results-title">Recent search results for:</span>
                                    <span class="result-code">
                                        {{ recentPreviewQuery }}
                                    </span>
                                </template>
                            </div>

                            <SearchSortToolbar
                                ref="searchSortToolbar"
                                :sort-by="sortBy"
                                :sort-dir="sortDir"
                                :is-loading="isLoading"
                                listbox-id="global-search-sort-listbox"
                                @select-sort="onToolbarSelectSort"
                                @toggle-direction="onToolbarToggleDirection"
                            />
                        </div>

                        <!-- Scrollable body: recent searches, confirmation, or results -->
                        <div ref="searchModalBody" class="search-modal-body">
                            <!--
                        Recent searches
                        <div v-if="shouldShowRecentSearches" class="search-results recent-searches">
                            <div class="search-group-header">
                                <e-icon icon="clock-rotate-left" size="is-small" />
                                <span>Recent Searches</span>
                            </div>

                            <div
                                v-for="(search, index) in recentSearches"
                                :key="`recent-${index}`"
                                class="search-result recent-search-item"
                                :class="{ 'is-selected': selectedRecentIndex === index }"
                                tabindex="0"
                                @click="executeRecentSearch(search.query)"
                                @mouseenter="selectedRecentIndex = index"
                                @focusin="selectedRecentIndex = index"
                            >
                                <e-icon icon="clock-rotate-left" size="is-small" class="result-icon" />
                                <div class="result-content">
                                    <span class="result-title">{{ search.query }}</span>
                                </div>
                                <e-button
                                    class="remove-recent-btn"
                                    native-type="button"
                                    aria-label="Remove from recent searches"
                                    @click.stop="handleRemoveRecentSearch(search.id)"
                                >
                                    <e-icon icon="times" size="is-small" />
                                </e-button>
                            </div>

                            <div class="recent-searches-footer">
                                <e-button
                                    native-type="button"
                                    class="footer-action clear-recent-btn"
                                    @click="handleClearRecentSearches"
                                >
                                    <e-icon icon="trash-alt" size="is-small" />
                                    <span class="footer-label">Clear recent</span>
                                </e-button>
                                <span class="footer-divider" />
                                <e-button
                                    native-type="button"
                                    class="footer-action advanced-search-btn"
                                    @click="goToAdvancedSearch"
                                >
                                    <span class="footer-label">Advanced</span>
                                    <e-icon icon="arrow-right" size="is-small" />
                                </e-button>
                            </div>
                        </div>

                        Clear confirmation dialog
                        <div v-else-if="showClearConfirmation" class="search-results clear-confirmation">
                            <div class="confirmation-content">
                                <p>Clear all recent searches?</p>
                                <div class="confirmation-actions">
                                    <e-button native-type="button" class="btn-cancel" @click="cancelClearRecentSearches">
                                        Cancel
                                    </e-button>
                                    <e-button native-type="button" class="btn-confirm" @click="confirmClearRecentSearches">
                                        Clear
                                    </e-button>
                                </div>
                            </div>
                        </div>
                        -->

                            <!-- Results container (typed search, or last-search preview when input is empty) -->
                            <div
                                v-if="hasResults || isLoading || searchQuery.length >= minQueryLength || isRecentPreviewVisible"
                                class="search-results"
                            >
                                <!-- Full spinner only when there is nothing to show yet (avoids flashing on "View more") -->
                                <div
                                    v-if="isLoading && !hasResults"
                                    class="search-loading"
                                    role="status"
                                    aria-live="polite"
                                >
                                    <ProgressSpinner
                                        class="search-loading-progress-spinner"
                                        style="width: 24px; height: 24px;"
                                        stroke-width="3"
                                        animation-duration=".75s"
                                        :value="0.75"
                                        color="#e6e6e6"
                                    />
                                    <p class="search-loading-title">Searching...</p>
                                </div>

                                <template v-else-if="hasResults">
                                    <div
                                        v-for="(items, type) in groupedResults"
                                        :key="type"
                                        class="search-group"
                                        :class="{ 'search-group--loading-more': isLoadingMore }"
                                    >
                                        <div class="search-group-header mb-4">
                                            <!-- <e-icon :icon="getTypeIcon(type)" size="is-small" /> -->
                                            <span>{{ getTypeLabel(type) }}</span>
                                            <span v-if="showResultCount(type)" class="result-count">
                                                Showing {{ items.length }} of {{ formatTypeFilterCount(getTotalHits(type)) }}
                                            </span>
                                        </div>

                                        <router-link
                                            v-for="(item, index) in items"
                                            :key="item.id"
                                            :to="item.url"
                                            class="search-result search-result--snippet-aligned"
                                            :class="{ 'is-selected': isSelected(type, index) }"
                                            @click="closeSearch"
                                            @mouseenter="selectItem(type, index)"
                                            @focus="selectItem(type, index)"
                                        >
                                            <div class="result-top-row">
                                                <span class="result-icon-box" aria-hidden="true">
                                                    <e-icon :icon="getTypeIcon(type, item)" size="is-small" class="result-icon" />
                                                </span>
                                                <div class="result-content">
                                                    <div class="result-title-row">
                                                        <span class="result-title">{{ getDisplayTitleParts(item, type).title }}</span>
                                                        <template v-if="type === 'users'">
                                                            <span v-if="getUserTitlePills(item).id" class="result-code">{{ getUserTitlePills(item).id }}</span>
                                                            <span v-if="getUserTitlePills(item).role" class="result-code">{{ getUserTitlePills(item).role }}</span>
                                                        </template>
                                                        <span v-else-if="getDisplayTitleParts(item, type).code" class="result-code">{{
                                                            getDisplayTitleParts(item, type).code
                                                        }}</span>
                                                    </div>
                                                    <span
                                                        v-if="usesCaptionPills(type) && (getResultCaptionParts(item, type).sectionCaption || getResultCaptionParts(item, type).courseTitle || getResultCaptionParts(item, type).code)"
                                                        class="result-caption-row"
                                                    >
                                                        <span
                                                            v-if="getResultCaptionParts(item, type).sectionCaption"
                                                            class="result-caption"
                                                        >{{ getResultCaptionParts(item, type).sectionCaption }}</span>
                                                        <span
                                                            v-if="getResultCaptionParts(item, type).courseTitle"
                                                            class="result-code"
                                                        >{{ getResultCaptionParts(item, type).courseTitle }}</span>
                                                        <span
                                                            v-if="getResultCaptionParts(item, type).code"
                                                            class="result-code"
                                                        >{{ getResultCaptionParts(item, type).code }}</span>
                                                    </span>
                                                    <span
                                                        v-else-if="getResultCaptionParts(item, type).caption"
                                                        class="result-caption"
                                                    >{{ getResultCaptionParts(item, type).caption }}</span>
                                                </div>
                                            </div>
                                            <!-- Snippet on its own row so its left edge aligns with the icon -->
                                            <!-- eslint-disable-next-line vue/no-v-html -->
                                            <span
                                                v-if="getResultSnippet(item, type)"
                                                class="result-snippet result-snippet-aligned"
                                                v-html="getResultSnippet(item, type)"
                                            />
                                        </router-link>

                                        <button
                                            v-if="showViewMoreButton(type, items)"
                                            type="button"
                                            class="view-all-link"
                                            :disabled="isLoadingMore"
                                            :aria-busy="isLoadingMore"
                                            aria-label="View more results for this category"
                                            @click="onViewMoreClick(type)"
                                        >
                                            <span>{{ isLoadingMore ? 'Loading...' : 'View more' }}</span>
                                            <e-icon
                                                v-if="isLoadingMore"
                                                icon="spinner"
                                                size="is-small"
                                                class="loading-icon search-loading-more-icon"
                                            />
                                            <e-icon v-else icon="chevron-right" size="is-small" />
                                        </button>
                                        <router-link
                                            v-else-if="showViewAllLink(type, items)"
                                            :to="getViewAllLink(type)"
                                            class="view-all-link"
                                            @click="closeSearch"
                                        >
                                            <span>View all</span>
                                            <e-icon icon="chevron-right" size="is-small" />
                                        </router-link>
                                    </div>
                                </template>

                                <div
                                    v-else-if="searchError"
                                    class="search-error"
                                >
                                    <e-icon icon="exclamation-triangle" size="is-medium" class="error-icon" />
                                    <p>{{ searchError }}</p>
                                </div>

                                <div
                                    v-else-if="hasSearched && searchQuery.length >= minQueryLength && !isLoading && !isLoadingMore"
                                    class="search-empty"
                                >
                                    <p>No results found for "{{ searchQuery }}"</p>
                                    <div class="search-empty-tips">
                                        <p>Check your spelling</p>
                                        <p>Try more general keywords</p>
                                        <p>Try different keywords</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Modal footer -->
                        <div class="search-modal-footer">
                            <e-button native-type="button" type="secondary" @click="closeSearch">Cancel</e-button>
                            <e-button native-type="button" class="btn-search" @click="goToAdvancedSearch">Advanced Search</e-button>
                        </div>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script>
import debounce from 'lodash/debounce.js';
import ProgressSpinner from 'primevue/progressspinner';
import SearchSortToolbar from '@/components/search/SearchSortToolbar.vue';
import { useRecentSearches } from '@/composables/useRecentSearches.js';
import { useTypeFilters } from '@/composables/useTypeFilters.js';
import axios from '@/plugins/axios.js';
import {
    appendSortToSearchParams,
    mergeSearchResultsSortIntoUrlQuery,
    normalizeSearchSortState,
    SEARCH_SORT_FALLBACK_MESSAGE,
} from '@/search/searchSortOptions.js';
import { formatTypeFilterCountDisplay } from './GlobalSearch/formatTypeFilterCount.js';

/**
 * Modal search: per-type hit limit from /api/global-search. Each "View more" adds this many
 * more hits per type until the max; then "View all" links to the full search page.
 */
const MODAL_PER_TYPE_LIMIT_INITIAL = 5;
const MODAL_PER_TYPE_LIMIT_STEP = 5;
const MODAL_PER_TYPE_LIMIT_MAX = 15;

/** Result types that show course title/code as separate pills in the caption row */
const RESULT_TYPES_WITH_CAPTION_PILLS = ['activities', 'sections', 'announcements', 'activity_updates', 'discussions'];

export default {
    name: 'GlobalSearch',

    components: {
        SearchSortToolbar,
        ProgressSpinner,
    },

    setup() {
        const {
            recentSearches,
            isLoading: isLoadingRecent,
            loadSearches,
            saveSearch,
            removeSearch,
            clearAll,
        } = useRecentSearches();

        const {
            selectedTypes,
            availableTypes,
            hasActiveFilters,
            typesForApi,
            isSelected,
            toggleType,
            clearAll: clearTypeFilters,
            setTypeCounts,
            toUrlParam,
        } = useTypeFilters();

        return {
            recentSearches,
            isLoadingRecent,
            loadRecentSearches: loadSearches,
            saveRecentSearch: saveSearch,
            // Type filters
            selectedTypes,
            availableTypes,
            hasActiveFilters,
            typesForApi,
            isTypeSelected: isSelected,
            toggleTypeFilter: toggleType,
            clearTypeFilters,
            setTypeCounts,
            typesToUrlParam: toUrlParam,
        };
    },

    data: () => ({
        searchQuery: '',
        results: null,
        isOpen: false,
        isLoading: false,
        /** True while fetching additional hits for "View more" (keeps list visible; no full-panel spinner). */
        isLoadingMore: false,
        hasSearched: false,
        searchError: null,
        selectedType: null,
        selectedIndex: 0,
        minQueryLength: 2,
        abortController: null,
        /** Last saved query shown as preview when the input is empty (not the same as typing in the box). */
        recentPreviewQuery: null,
        /**
         * In-modal limit per result type.
         * Starts at 5 and becomes 10 → 15 only for the section whose "View more" is clicked.
         */
        modalPerTypeLimitByType: {},
        /** Per result-type: how many times the user clicked "View more" (see VIEW_MORE_CLICKS_BEFORE_VIEW_ALL). */
        viewMoreClicksByType: {},
        selectedRecentIndex: -1,
        showClearConfirmation: false,
        sortBy: 'relevance',
        sortDir: 'desc',
        /** Query string for the last completed `/api/global-search` response (used for Enter vs stale results). */
        lastSearchQuery: '',
    }),

    computed: {
        isMac() {
            // Use modern API with fallback to deprecated platform
            if (navigator.userAgentData) {
                return navigator.userAgentData.platform.toUpperCase().indexOf('MAC') >= 0;
            }
            return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        },

        hasResults() {
            if (!this.results?.results) return false;
            return Object.values(this.results.results).some(items => items.length > 0);
        },

        /**
         * Divider under type filters: show when there are hits, or when a typed search finished with
         * no hits / error (so it sits above “No results found…” or the error message). Hidden while loading.
         */
        showResultsAreaDivider() {
            if (this.isRecentPreviewVisible) {
                return this.isLoading || this.isLoadingMore || this.hasResults;
            }

            if (this.searchQuery.length < this.minQueryLength) {
                return false;
            }

            if (this.hasResults) {
                return true;
            }

            return this.hasSearched && !this.isLoading && !this.isLoadingMore;
        },

        groupedResults() {
            if (!this.results?.results) return {};
            // Filter out empty categories and maintain order
            const order = [
                'courses',
                'activities',
                'sections',
                'announcements',
                'activity_updates',
                'discussions',
                'users',
                'help_articles',
            ];
            const filtered = {};
            order.forEach(type => {
                if (this.results.results[type]?.length > 0) {
                    filtered[type] = this.results.results[type];
                }
            });
            return filtered;
        },

        flatResults() {
            const flat = [];
            Object.entries(this.groupedResults).forEach(([type, items]) => {
                items.forEach((item, index) => {
                    flat.push({ type, index, item });
                });
            });
            return flat;
        },

        /** Same idea as Search results page: show a "Recent" label when listing hits from the last saved query. */
        isRecentPreviewVisible() {
            return !!this.recentPreviewQuery && !this.searchQuery.trim();
        },

        estimatedTotalHits() {
            return this.results?.meta?.estimatedTotalHits || {};
        },

        showTypeFilters() {
            return (
                this.searchQuery.length >= this.minQueryLength &&
                (this.isLoading || this.isLoadingMore || this.hasSearched || this.hasResults)
            );
        },

        hasFiltersSelected() {
            return this.selectedTypes.length > 0;
        },

        typeFilterTriggerLabel() {
            if (this.selectedTypes.length === 0) {
                return 'All types';
            }
            if (this.selectedTypes.length === 1) {
                return this.getTypeLabel(this.selectedTypes[0]);
            }
            return `${this.selectedTypes.length} types`;
        },

        /** True when the current input matches the last finished search (Enter should open selection, not refetch). */
        matchesLastSearch() {
            const q = this.searchQuery.trim();
            if (q.length < this.minQueryLength) {
                return false;
            }
            return this.hasSearched && q === this.lastSearchQuery;
        },

        showSortToolbar() {
            if (!this.hasResults) {
                return false;
            }

            return this.searchQuery.length >= this.minQueryLength || this.isRecentPreviewVisible;
        },
    },

    watch: {
        selectedTypes: {
            handler: 'onTypeFilterChange',
            deep: true,
        },
        searchQuery(val) {
            if (val?.trim() && this.recentPreviewQuery) {
                this.recentPreviewQuery = null;
            } else if (!val?.trim() && this.isOpen) {
                this.loadMostRecentPreviewIfNeeded();
            }
        },
    },

    created() {
        this.debouncedSearch = debounce(this.performSearch, 300);
        document.addEventListener('keydown', this.handleGlobalKeydown);
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.handleGlobalKeydown);
        if (this.debouncedSearch && typeof this.debouncedSearch.cancel === 'function') {
            this.debouncedSearch.cancel();
        }
        if (this.abortController) {
            this.abortController.abort();
        }
    },

    methods: {
        handleGlobalKeydown(e) {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                this.openSearch();
            }
        },

        async openSearch() {
            this.isOpen = true;
            await this.loadRecentSearches();
            await this.loadMostRecentPreviewIfNeeded();
            this.$nextTick(() => {
                this.$refs.searchInput?.focus();
            });
        },

        clearSearch() {
            if (this.abortController) {
                this.abortController.abort();
            }
            if (this.debouncedSearch && typeof this.debouncedSearch.cancel === 'function') {
                this.debouncedSearch.cancel();
            }
            const hadInput = this.searchQuery.trim().length > 0;
            this.searchQuery = '';
            this.recentPreviewQuery = null;
            this.results = null;
            this.lastSearchQuery = '';
            this.hasSearched = false;
            this.searchError = null;
            this.isLoading = false;
            this.isLoadingMore = false;
            this.selectedType = null;
            this.selectedIndex = 0;
            this.viewMoreClicksByType = {};
            this.$refs.searchSortToolbar?.closeDropdown?.();
            this.modalPerTypeLimitByType = {};
            this.setTypeCounts({}, true);
            if (!hadInput && this.recentSearches.length > 0) {
                this.loadMostRecentPreviewIfNeeded();
            }
            this.$nextTick(() => {
                this.$refs.searchInput?.focus();
            });
        },

        closeSearch() {
            this.isOpen = false;
            this.searchQuery = '';
            this.recentPreviewQuery = null;
            this.results = null;
            this.lastSearchQuery = '';
            this.hasSearched = false;
            this.searchError = null;
            this.selectedType = null;
            this.selectedIndex = 0;
            this.viewMoreClicksByType = {};
            this.selectedRecentIndex = -1;
            this.showClearConfirmation = false;
            this.sortBy = 'relevance';
            this.sortDir = 'desc';
            this.$refs.searchSortToolbar?.closeDropdown?.();
            this.modalPerTypeLimitByType = {};
            this.isLoadingMore = false;
            // Reset type filter counts and selection so reopening shows a clean state
            this.setTypeCounts({}, true);
            this.clearTypeFilters();
        },

        async loadMostRecentPreviewIfNeeded() {
            if (this.searchQuery.trim().length >= this.minQueryLength) {
                return;
            }
            if (!this.recentSearches.length) {
                return;
            }
            const q = this.recentSearches[0].query?.trim();
            if (!q || q.length < this.minQueryLength) {
                return;
            }
            if (this.isLoading || this.isLoadingMore) {
                return;
            }
            this.recentPreviewQuery = q;
            await this.fetchRecentPreviewResults();
        },

        async fetchRecentPreviewResults(resetLimit = true) {
            const q = this.recentPreviewQuery?.trim();
            if (!q || q.length < this.minQueryLength) {
                return;
            }

            if (resetLimit) {
                this.modalPerTypeLimitByType = {};
            }

            await this.runGlobalSearchRequest(q, { saveToRecent: false, previewErrorClear: true });
        },

        /**
         * Shared POST /api/global-search for typed search, recent preview, and "View more" refetches.
         */
        async runGlobalSearchRequest(
            query,
            {
                saveToRecent = false,
                previewErrorClear = false,
                isLoadMore = false,
                targetType = null,
                limitOverride = null,
            } = {}
        ) {
            if (this.abortController) {
                this.abortController.abort();
            }
            this.abortController = new AbortController();

            if (isLoadMore) {
                this.isLoadingMore = true;
            } else {
                // Drop previous hits so `hasResults` is false while fetching; otherwise
                // `isLoading && !hasResults` never shows "Searching..." after a prior result set.
                this.results = null;
                this.isLoading = true;
                this.hasSearched = false;
            }
            this.searchError = null;

            let requestCancelled = false;
            let requestSucceeded = false;

            try {
                const requestData = {
                    query,
                    limit: Number.isInteger(limitOverride) ? limitOverride : this.getRequestLimit(targetType),
                };
                appendSortToSearchParams(requestData, this.sortBy, this.sortDir);

                if (targetType) {
                    requestData.types = [targetType];
                } else if (this.typesForApi.length > 0) {
                    requestData.types = this.typesForApi;
                }

                const { data } = await axios.post('/api/global-search', requestData, {
                    signal: this.abortController.signal,
                });
                if (targetType && isLoadMore && this.results?.results) {
                    const existingResults = this.results.results || {};
                    const incomingResults = data.results || {};
                    const mergedResults = {
                        ...existingResults,
                        [targetType]: incomingResults[targetType] ?? existingResults[targetType] ?? [],
                    };

                    const existingMeta = this.results.meta || {};
                    const incomingMeta = data.meta || {};
                    const existingEstimatedTotalHits = existingMeta.estimatedTotalHits || {};
                    const incomingEstimatedTotalHits = incomingMeta.estimatedTotalHits || {};
                    const mergedEstimatedTotalHits = {
                        ...existingEstimatedTotalHits,
                    };
                    if (Object.hasOwn(incomingEstimatedTotalHits, targetType)) {
                        mergedEstimatedTotalHits[targetType] = incomingEstimatedTotalHits[targetType];
                    }

                    this.results = {
                        ...this.results,
                        ...data,
                        results: mergedResults,
                        meta: {
                            ...existingMeta,
                            ...incomingMeta,
                            estimatedTotalHits: mergedEstimatedTotalHits,
                        },
                    };
                } else {
                    this.results = data;
                }
                this.lastSearchQuery = query.trim();
                this.resetSelection();
                requestSucceeded = true;

                if (data.meta?.sort_fallback === true) {
                    this.$edverumToast.open({
                        message: data.meta.sort_fallback_message || SEARCH_SORT_FALLBACK_MESSAGE,
                        type: 'is-warning',
                        duration: 5000,
                    });
                    const fb = normalizeSearchSortState('relevance', 'desc');
                    this.sortBy = fb.sortBy;
                    this.sortDir = fb.sortDir;
                }

                if (data.meta?.estimatedTotalHits) {
                    const isFullSearch = !targetType && this.typesForApi.length === 0;
                    this.setTypeCounts(data.meta.estimatedTotalHits, isFullSearch);
                }

                if (saveToRecent && this.hasResults) {
                    const totalResults = Object.values(data.results || {}).reduce(
                        (sum, items) => sum + (items?.length || 0),
                        0
                    );
                    this.saveRecentSearch(this.searchQuery, totalResults);
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    requestCancelled = true;
                } else {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error('Global search request failed:', error);
                    }
                    this.results = null;
                    this.searchError = 'Search failed. Please try again.';
                    if (previewErrorClear && !this.searchQuery.trim()) {
                        this.recentPreviewQuery = null;
                    }
                }
            } finally {
                this.isLoading = false;
                this.isLoadingMore = false;
                if (!requestCancelled) {
                    this.hasSearched = true;
                }
            }

            return requestSucceeded;
        },

        onToolbarSelectSort(value) {
            this.sortBy = value;
            this.onSearchSortChange();
        },

        onToolbarToggleDirection() {
            if (this.sortBy === 'relevance' || this.isLoading) {
                return;
            }
            this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
            this.onSearchSortChange();
        },

        onSearchSortChange() {
            const normalized = normalizeSearchSortState(this.sortBy, this.sortDir);
            this.sortBy = normalized.sortBy;
            this.sortDir = normalized.sortDir;
            this.performSearch();
        },

        async performSearch() {
            const typedQuery = this.searchQuery.trim();
            const previewQuery = this.recentPreviewQuery?.trim() || '';
            const query = typedQuery.length >= this.minQueryLength ? typedQuery : previewQuery;

            if (query.length < this.minQueryLength) {
                this.results = null;
                this.hasSearched = false;
                this.modalPerTypeLimitByType = {};
                this.isLoadingMore = false;
                return;
            }

            const isPreviewSearch = Boolean(previewQuery) && typedQuery.length < this.minQueryLength;
            if (!isPreviewSearch) {
                this.recentPreviewQuery = null;
            }
            this.modalPerTypeLimitByType = {};

            await this.runGlobalSearchRequest(query, {
                saveToRecent: !isPreviewSearch,
                previewErrorClear: isPreviewSearch,
            });
        },

        async onViewMoreClick(type) {
            if (this.isLoadingMore) {
                return;
            }
            if (!type) {
                return;
            }
            const previousLimit = this.getTypeModalLimit(type);
            if (previousLimit >= MODAL_PER_TYPE_LIMIT_MAX) {
                return;
            }
            const q =
                this.searchQuery.trim().length >= this.minQueryLength
                    ? this.searchQuery.trim()
                    : this.recentPreviewQuery?.trim();
            if (!q || q.length < this.minQueryLength) {
                return;
            }

            const nextLimit = Math.min(MODAL_PER_TYPE_LIMIT_MAX, previousLimit + MODAL_PER_TYPE_LIMIT_STEP);
            this.modalPerTypeLimitByType = {
                ...this.modalPerTypeLimitByType,
                [type]: nextLimit,
            };

            const isPreview = Boolean(this.recentPreviewQuery?.trim()) && !this.searchQuery.trim();
            const didLoad = await this.runGlobalSearchRequest(q, {
                saveToRecent: false,
                previewErrorClear: isPreview,
                isLoadMore: true,
                targetType: type,
                limitOverride: nextLimit,
            });
            if (!didLoad) {
                this.modalPerTypeLimitByType = {
                    ...this.modalPerTypeLimitByType,
                    [type]: previousLimit,
                };
            }
        },

        resetSelection() {
            const firstType = Object.keys(this.groupedResults)[0];
            this.selectedType = firstType || null;
            this.selectedIndex = 0;
        },

        navigateDown() {
            const len = this.flatResults.length;
            if (len === 0) {
                return;
            }
            const currentFlatIndex = this.getCurrentFlatIndex();
            const next = currentFlatIndex < len - 1 ? this.flatResults[currentFlatIndex + 1] : this.flatResults[0];
            this.selectedType = next.type;
            this.selectedIndex = next.index;
            this.scrollActiveResultIntoView();
        },

        navigateUp() {
            const len = this.flatResults.length;
            if (len === 0) {
                return;
            }
            const currentFlatIndex = this.getCurrentFlatIndex();
            const prev = currentFlatIndex > 0 ? this.flatResults[currentFlatIndex - 1] : this.flatResults[len - 1];
            this.selectedType = prev.type;
            this.selectedIndex = prev.index;
            this.scrollActiveResultIntoView();
        },

        scrollActiveResultIntoView() {
            this.$nextTick(() => {
                const body = this.$refs.searchModalBody;
                if (!body || typeof body.querySelector !== 'function') {
                    return;
                }
                const active = body.querySelector('.search-result.is-selected');
                active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            });
        },

        getCurrentFlatIndex() {
            return this.flatResults.findIndex(r => r.type === this.selectedType && r.index === this.selectedIndex);
        },

        selectItem(type, index) {
            this.selectedType = type;
            this.selectedIndex = index;
        },

        isSelected(type, index) {
            return this.selectedType === type && this.selectedIndex === index;
        },

        selectResult() {
            if (this.selectedType && this.groupedResults[this.selectedType]) {
                const item = this.groupedResults[this.selectedType][this.selectedIndex];
                if (item?.url) {
                    this.$router.push(item.url);
                    this.closeSearch();
                }
            }
        },

        /**
         * Enter: run search immediately when the box does not match the last response (e.g. debounce not fired yet),
         * otherwise open the highlighted hit (same as arrow + enter).
         * Do not navigate on the same keypress that triggered a new fetch — user should see the list first.
         */
        async handleSearchEnter() {
            const q = this.searchQuery.trim();
            if (q.length < this.minQueryLength) {
                return;
            }

            if (this.debouncedSearch && typeof this.debouncedSearch.cancel === 'function') {
                this.debouncedSearch.cancel();
            }

            const alreadyHadCurrentResults = this.matchesLastSearch;

            if (!alreadyHadCurrentResults) {
                await this.performSearch();
                return;
            }

            await this.$nextTick();
            if (this.isLoading || this.isLoadingMore) {
                return;
            }

            this.selectResult();
        },

        goToAdvancedSearch() {
            const queryParams = {};
            if (this.searchQuery?.trim()) {
                queryParams.query = this.searchQuery.trim();
            } else if (this.recentPreviewQuery?.trim()) {
                queryParams.query = this.recentPreviewQuery.trim();
            }
            const typesParam = this.typesToUrlParam();
            if (typesParam) {
                queryParams.types = typesParam;
            } else if (this.selectedTypes?.length === 1) {
                const [singleType] = this.selectedTypes;
                queryParams.type = singleType;
            }
            mergeSearchResultsSortIntoUrlQuery(queryParams, this.sortBy, this.sortDir);
            this.closeSearch();
            this.$router
                .push({
                    name: 'search-results',
                    query: queryParams,
                })
                .catch(() => {});
        },

        getDisplayTitle(item, type) {
            if (type === 'users') {
                return item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim();
            }
            return item.title || item.name || '';
        },

        /**
         * Split display title into main title and parenthetical code (e.g. "Name (CODE)").
         * Returns { title, code }; code is null when there is no trailing parenthetical part.
         * For users, code is not used; use getUserTitlePills instead for id/role pills.
         */
        getDisplayTitleParts(item, type) {
            if (type === 'users') {
                const title = this.getDisplayTitle(item, type);
                return { title, code: null };
            }
            const full = this.getDisplayTitle(item, type);
            const match = full.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
            if (match) {
                return { title: match[1].trim(), code: match[2].trim() };
            }
            return { title: full, code: null };
        },

        /** For user results: id and role shown as pills on the same line as the name. */
        getUserTitlePills(item) {
            return {
                id: item.external_id || null,
                role: item.role_names?.[0] || null,
            };
        },

        getTypeIcon(type, item = null) {
            // For activities, use activity-type-specific icons
            if (type === 'activities' && item?.activity_type) {
                return this.getActivityTypeIcon(item.activity_type);
            }

            const icons = {
                courses: 'book-alt',
                activities: 'file',
                sections: 'folder-open',
                announcements: 'megaphone',
                activity_updates: 'bell',
                discussions: 'comments',
                users: 'user',
                help_articles: 'question-circle',
            };
            return icons[type] || 'search';
        },

        getActivityTypeIcon() {
            return 'file';
        },

        getTypeLabel(type) {
            const labels = {
                courses: 'Courses',
                activities: 'Activities',
                sections: 'Sections',
                announcements: 'Announcements',
                activity_updates: 'Activity Updates',
                discussions: 'Discussions',
                users: 'Users',
                help_articles: 'Help Articles',
            };
            return labels[type] || type;
        },

        /**
         * Parses "Title (CODE)" into { courseTitle, code }. Returns { courseTitle: null, code: null } when empty.
         */
        parseCourseTitleAndCode(courseTitleStr) {
            const raw = (courseTitleStr || '').trim();
            if (!raw) return { courseTitle: null, code: null };
            const match = raw.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
            if (match) {
                return {
                    courseTitle: match[1].trim() || null,
                    code: match[2].trim() || null,
                };
            }
            return { courseTitle: raw, code: null };
        },

        /** True when this result type uses caption pills (sectionCaption + courseTitle + code). */
        usesCaptionPills(type) {
            return RESULT_TYPES_WITH_CAPTION_PILLS.includes(type);
        },

        /**
         * For types in RESULT_TYPES_WITH_CAPTION_PILLS: returns { sectionCaption, courseTitle, code }.
         * sectionCaption is "in [section]" (activities), "in [activity]" (activity_updates/discussions), or "in " (sections/announcements). courseTitle and code are shown as pills.
         * For other types: returns { caption: string, code: null }.
         */
        getResultCaptionParts(item, type) {
            if (type === 'activities') {
                const sectionCaption = item.section_title ? `in ${item.section_title}` : null;
                const { courseTitle, code } = this.parseCourseTitleAndCode(item.course_title);
                return { sectionCaption, courseTitle, code };
            }
            if (type === 'sections' || type === 'announcements') {
                const { courseTitle, code } = this.parseCourseTitleAndCode(item.course_title);
                const sectionCaption = courseTitle || code ? 'in ' : null;
                return { sectionCaption, courseTitle, code };
            }
            if (type === 'activity_updates' || type === 'discussions') {
                const sectionCaption = item.activity_title ? `in ${item.activity_title}` : null;
                const { courseTitle, code } = this.parseCourseTitleAndCode(item.course_title);
                return { sectionCaption, courseTitle, code };
            }
            if (type === 'users') {
                return { caption: null, code: null };
            }
            const caption = this.getResultCaption(item, type);
            return { caption, code: null };
        },

        getResultCaption(item, type) {
            if (this.usesCaptionPills(type)) {
                const { sectionCaption, courseTitle, code } = this.getResultCaptionParts(item, type);
                if (!sectionCaption && !courseTitle && !code) return null;
                const parts = [];
                if (sectionCaption) parts.push(sectionCaption);
                if (courseTitle) parts.push(courseTitle);
                if (code) parts.push(`(${code})`);
                return parts.join(' ');
            }

            if (type === 'users') {
                // Show external ID and role in caption
                const parts = [];
                if (item.external_id) {
                    parts.push(item.external_id);
                }
                if (item.role_names && item.role_names.length > 0) {
                    parts.push(item.role_names[0]);
                }
                return parts.length > 0 ? parts.join(' • ') : null;
            }

            if (type === 'help_articles') {
                // Show folder context
                return item.folder_title || null;
            }

            return null;
        },

        getResultSnippet(item, type) {
            if (!item._highlighted) return null;

            // Define priority order of fields to check for highlights
            const priorityMap = {
                courses: ['description', 'title'],
                activities: ['description', 'title', 'section_title', 'course_title'],
                sections: ['description', 'title', 'course_title'],
                announcements: ['body', 'title', 'course_title'],
                activity_updates: ['body', 'title', 'activity_title', 'course_title'],
                discussions: ['body', 'title', 'author_name'],
                users: ['email', 'first_name', 'last_name'],
                help_articles: ['body', 'summary', 'title'],
            };

            const fields = priorityMap[type] || [];
            const matchedField = fields.find(field => {
                const highlighted = item._highlighted[field];
                return highlighted && highlighted.includes('<em>') && field !== 'title';
            });

            if (!matchedField) return null;
            return this.formatSnippet(item._highlighted[matchedField]);
        },

        formatSnippet(text) {
            const maxLength = this.$isMobile ? 120 : 150;

            // Strip any HTML except <em> tags (security)
            const cleaned = text.replace(/<(?!\/?(em))[^>]+>/gi, '');

            // Find position of first <em> tag to center the snippet
            const firstEmIndex = cleaned.indexOf('<em>');
            const startPos = Math.max(0, firstEmIndex - 50);

            // Extract snippet
            let snippet = cleaned.substring(startPos, startPos + maxLength);

            // Trim to word boundary
            if (startPos > 0) {
                const firstSpace = snippet.indexOf(' ');
                if (firstSpace > 0) snippet = snippet.substring(firstSpace + 1);
            }

            if (cleaned.length > startPos + maxLength) {
                const lastSpace = snippet.lastIndexOf(' ');
                if (lastSpace > 0) snippet = snippet.substring(0, lastSpace);
            }

            return snippet;
        },

        getTotalHits(type) {
            return this.estimatedTotalHits[type] || 0;
        },

        formatTypeFilterCount(count) {
            return formatTypeFilterCountDisplay(count);
        },

        showResultCount(type) {
            const total = this.getTotalHits(type);
            const shown = this.results?.results[type]?.length || 0;
            return total > shown;
        },

        shouldShowViewAll(type, items) {
            const total = this.getTotalHits(type);
            // Show "View all" only if estimated total is greater than number of results shown
            return total > items.length;
        },

        getTypeModalLimit(type) {
            return this.modalPerTypeLimitByType[type] || MODAL_PER_TYPE_LIMIT_INITIAL;
        },

        getRequestLimit(targetType = null) {
            if (targetType) {
                return this.getTypeModalLimit(targetType);
            }
            const perTypeLimits = Object.values(this.modalPerTypeLimitByType);
            if (perTypeLimits.length === 0) {
                return MODAL_PER_TYPE_LIMIT_INITIAL;
            }
            return Math.max(MODAL_PER_TYPE_LIMIT_INITIAL, ...perTypeLimits);
        },

        /** "View more" refetches with a higher per-type limit until the modal max; then "View all". */
        showViewMoreButton(type, items) {
            return this.shouldShowViewAll(type, items) && this.getTypeModalLimit(type) < MODAL_PER_TYPE_LIMIT_MAX;
        },

        /** At max in-modal limit, navigate to the full search page when more hits exist. */
        showViewAllLink(type, items) {
            return this.shouldShowViewAll(type, items) && this.getTypeModalLimit(type) >= MODAL_PER_TYPE_LIMIT_MAX;
        },

        getViewAllLink(type) {
            const q = (this.searchQuery || '').trim() || (this.recentPreviewQuery || '').trim();
            const queryParams = {
                query: q,
                type,
            };
            mergeSearchResultsSortIntoUrlQuery(queryParams, this.sortBy, this.sortDir);

            return {
                path: '/search',
                query: queryParams,
            };
        },

        /**
         * Re-run search when type filters change
         */
        onTypeFilterChange() {
            if (this.searchQuery.length >= this.minQueryLength) {
                this.performSearch();
            } else if (this.recentPreviewQuery) {
                this.fetchRecentPreviewResults();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~assets/scss/toolbox.scss';
@import './GlobalSearch/_shared.scss';
@import './GlobalSearch/_global-search-variables.scss';

// ---- Trigger (toolbar) ----
.global-search {
    .search-trigger {
        display: flex;
        align-items: center;
        gap: $space-7;
        padding: $space-7 $space-4;
        background: var(--surface-hover, #{$surface-50});
        border: 1px solid var(--surface-border, #{$surface-border});
        border-radius: $radius;
        cursor: pointer;
        color: var(--text-color-secondary, #{$grey-light});
        font-size: $size-7;
        transition: all $search-transition-duration ease;

        &:hover {
            background: var(--surface-card, #{$white});
            border-color: var(--surface-border, #{$surface-border});
        }

        .trigger-text {
            @include until($desktop) {
                display: none;
            }
        }
    }

    .search-shortcut {
        display: none;
        font-size: $size-7;
        color: var(--text-color-secondary, #{$grey-light});
        gap: $radius-small;
        margin-left: $space-4;

        @include from($desktop) {
            display: flex;
            align-items: center;
        }

        kbd {
            background: var(--surface-hover, #{$surface-50});
            padding: $radius-small 5px;
            border-radius: $radius-small;
            font-family: inherit;
            font-size: $very-small-font-size;
        }
    }

    .search-trigger--icon-only {
        padding: 0;
        background: transparent;
        border: none;
        border-radius: $radius;
        color: var(--text-color, #{$text-color});
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;

        .search-trigger-icon {
            color: inherit;
            width: $navbar-search-icon-size !important;
            height: $navbar-search-icon-size !important;
            font-size: $navbar-search-icon-size !important;
            display: inline-flex !important;
            align-items: center;
            justify-content: center;
        }

        &:hover {
            background: var(--surface-hover, #{$surface-50});
            color: var(--primary-color, #{$primary});
        }
    }
}

// ---- Modal ----
.search-modal-overlay {
    position: fixed;
    inset: 0;
    background: var(--maskbg, rgba(0, 0, 0, 0.5));
    backdrop-filter: blur(4px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 10vh;
    z-index: 1000;
}

.search-loading-title {
    margin: 0;
    color:#64748B;
    font-size: 14px;
    font-weight: 500;
}

.search-modal {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 720px;
    max-height: 85vh;
    background: var(--surface-card, #{$white});
    border-radius: $radius-large;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    margin: 0 $space-5;
    padding: $space-5;
}

.search-modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    // Keeps a gap above the first hit when scrollIntoView (e.g. wrap last → first) aligns it to the scrollport top.
    scroll-padding-top: $space-4;
}

.search-modal-footer {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: $space-4;
    padding-top: $space-5;
    margin-top: $space-5;
    border-top: 1px solid var(--surface-border, #{$surface-border});
}

// ---- Type filter chips: see _shared.scss ----

.type-filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $space-7 $space-5;
    border-bottom: 1px solid var(--surface-border, #{$surface-border});
    gap: $space-7;
}

.search-type-dropdown {
    display: flex;
    width: 100%;

    :deep(.dropdown-menu) {
        min-width: 100%;
        left: 0;
    }
}

.type-filter-trigger {
    display: inline-flex;
    align-items: center;
    gap: $size-8;
    justify-content: space-between;
    width: 100%;
    padding: 0 $space-4;
    height: 38px;
    border: 1px solid var(--surface-border, #{$surface-border});
    border-radius: $radius;
    background: var(--surface-card, #{$white});
    color: var(--text-color, #{$text-color});
    font-size: $small-font-size;
    font-weight: $weight-medium;
    cursor: pointer;
    transition: all $search-transition-duration ease;

    &:hover {
        border-color: var(--surface-border, #{$surface-border});
        color: var(--text-strong, #{$black});
    }
}

.type-filter-trigger-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

// Type filter dropdown (modal-specific; chip styles in _shared.scss)
.type-filter-dropdown-panel {
    padding: $space-4;
    min-width: 240px;
    background: var(--surface-card, #{$white});
}

.type-filter-dropdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: $small-font-size;
    font-weight: $weight-semibold;
    color: var(--text-strong, #{$text-color});
    margin-bottom: $space-7;
}

.type-filter-reset {
    background: none;
    border: none;
    color: var(--primary-color, #{$primary});
    font-size: $small-font-size;
    cursor: pointer;
    padding: $radius-small;

    &:hover {
        text-decoration: underline;
    }
}

.type-filter-dropdown-list {
    display: flex;
    flex-direction: column;
    gap: $radius-small;
    max-height: 280px;
    overflow-y: auto;
}

.results-divider {
    margin-top: $size-5;
    border-top: 1px solid var(--surface-border, #{$surface-border});

    &:first-child {
        display: none;
    }
}

.global-search-sort-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    margin-top: $space-3;
    font-weight: 700;
}

.global-search-sort-toolbar .search-results-title-group {
    display: inline-flex;
    align-items: center;
    gap: $space-2;
    min-width: 0;
}

// Sort controls: SearchSortToolbar.vue

// Recent row uses .search-group-header + .result-count from _shared.scss (same as “Showing x of y”).
// It is not the first child of .search-results (divider is), so undo the extra top rule from _shared.
.search-results > .search-group-header.recent-preview-banner {
    border-top: none;
    padding-top: 0;

    .recent-preview-label {
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        gap: $space-2;
        min-width: 0;
    }

    .result-code {
        display: inline-flex;
        align-items: center;
        line-height: 1.2;
        max-width: 100%;
    }

    .result-count {
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

// Search input wrapper: see _shared.scss

@include until($desktop) {
    .search-input-wrapper {
        .search-input {
            min-width: 0;
        }

        .close-button {
            margin: 0 $search-input-icon-margin 0 0;
        }
    }
}

@include until($tablet) {
    .search-modal {
        max-height: 90vh;
    }

    .type-filters {
        padding: $space-7 $space-5;
    }

    .type-filter-dropdown-panel {
        min-width: min(260px, calc(100vw - 3rem));
        max-width: calc(100vw - 2rem);
    }

    .recent-searches {
        .recent-searches-footer {
            gap: $space-7;
            padding: $space-7 $space-4;
        }

        .footer-action {
            font-size: 11px;
            gap: $size-8;
            padding: 4px $space-7;
        }

        .footer-label {
            white-space: nowrap;
        }
    }
}

// Modal-specific: scrollable area (list container + title from _shared.scss)
.search-results {
    overflow-x: hidden;
    overflow-y: auto;
    margin-top: $space-4;
}

button.view-all-link {
    width: 100%;
    background: none;
    border: none;
    border-radius: 0;
    cursor: pointer;
    appearance: none;
    font: inherit;
    text-align: left;
    color: #335CE5;
    font-size: 13px;
    font-weight: 700;

    &:disabled {
        cursor: wait;
        opacity: 0.85;
    }
}

.search-loading-more-icon {
    animation: spin 1s linear infinite;
}

.search-group--loading-more {
    transition: opacity 0.15s ease;
}

.search-loading {
    padding: $space-5 $space-5 $space-7;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100px;
    gap: 10px;

    .search-loading__title {
        margin: 0;
        width: 100%;
        text-align: center;
    }

    :deep(.search-loading-progress-spinner) {
        .p-progressspinner-circle {
            stroke: #e6e6e6;
        }
    }
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.search-empty {
    text-align: center;
    color: var(--text-color-secondary, #{$grey-light});

    .empty-icon {
        margin-bottom: $space-4;
        opacity: 0.5;
        @include search-icon-large;
    }

    p {
        margin: 0;
    }

    .search-empty-tips {
        background: $tag-info-background;
        border-radius: var(--message-border-radius, #{$radius});
        border: 1px solid var(--p-message-info-border-color, #bfdbfe);
        box-shadow: var(--p-message-info-shadow, 0 4px 8px 0 rgba(2, 5, 10, 0.04));
        margin-top: $space-5;
        padding: $space-2 $inputtext-padding-x;
        text-align: left;
        color: var(--p-primary-color, #{$tag-info-color});
        font-family: 'Inter', $family-sans-serif;
        font-size: $size-7;
        font-style: normal;
        font-weight: 500;
        line-height: 100%;
        letter-spacing: 0;
    }
}

.search-error {
    padding: $space-5;
    text-align: center;
    color: var(--p-red-500, #{$danger});

    .error-icon {
        margin-bottom: $space-4;
        @include search-icon-large;
    }

    p {
        margin: 0;
    }
}

// ---- Modal transition ----
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .search-modal,
.modal-leave-active .search-modal {
    transition: transform 0.2s ease;
}

.modal-enter-from .search-modal,
.modal-leave-to .search-modal {
    transform: scale(0.95) translateY(-10px);
}

// ---- Recent searches ----
.recent-searches {
    .recent-search-item {
        position: relative;

        .remove-recent-btn {
            opacity: 0;
            transition: opacity $search-transition-duration ease;
            background: none;
            border: none;
            cursor: pointer;
            padding: $space-7;
            color: var(--text-color-secondary, #{$iconfield-icon-color});
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            @include search-icon-medium;

            &:hover {
                color: var(--p-red-500, #{$danger});
            }
        }

        &:hover .remove-recent-btn,
        &.is-selected .remove-recent-btn {
            opacity: 1;
        }
    }

    .recent-searches-footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: $space-4;
        padding: $space-4 $space-5;
        border-top: 1px solid var(--surface-border, #{$surface-border});
    }

    .footer-divider {
        width: 1px;
        height: 18px;
        background: var(--surface-border, #{$surface-border});
        align-self: center;
    }

    .footer-action {
        background: none;
        border: none;
        color: var(--text-color-secondary, #{$grey-light});
        font-size: $size-7;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: $space-7;
        padding: $space-7 $space-4;
        border-radius: $radius-small;
        transition: all $search-transition-duration ease;
        line-height: 1;
        flex: 1 1 0;
        @include search-icon-small;
    }

    .clear-recent-btn:hover {
        color: var(--p-red-500, #{$danger});
        background: rgba($danger, 0.1);
    }

    .advanced-search-btn:hover {
        color: var(--primary-color, #{$primary});
        background: rgba($primary, 0.1);
    }
}

// ---- Clear confirmation ----
.clear-confirmation {
    .confirmation-content {
        padding: $space-5;
        text-align: center;

        p {
            margin: 0 0 $space-5 0;
            color: var(--text-color, #{$text-color});
            font-size: $size-6;
        }
    }

    .confirmation-actions {
        display: flex;
        justify-content: center;
        gap: $space-4;
    }

    .btn-cancel,
    .btn-confirm {
        padding: $space-7 $space-5;
        border-radius: $radius-small;
        font-size: $size-7;
        cursor: pointer;
        transition: all $search-transition-duration ease;
    }

    .btn-cancel {
        background: var(--surface-hover, #{$surface-50});
        border: 1px solid var(--surface-border, #{$surface-border});
        color: var(--text-color, #{$text-color});

        &:hover {
            background: var(--surface-border, #{$surface-border});
        }
    }

    .btn-confirm {
        background: var(--p-red-500, #{$danger});
        border: 1px solid var(--p-red-500, #{$danger});
        color: var(--primary-color-text, #{$white});

        &:hover {
            background: var(--p-red-600, #e5435f);
        }
    }
}

// Dark mode support (bridge PrimeVue and legacy dark mode selectors)
:global(.app-dark),
:root.dark-mode {
    .search-trigger {
        background: $grey-darker;
        border-color: $grey-dark;
        color: $grey-light;

        &:hover {
            background: $grey-dark;
        }

        kbd {
            background: $grey-dark;
        }

        &--icon-only {
            background: transparent;
            border: none;
            border-radius: $radius;
            color: $grey-light;

            &:hover {
                background: rgba($primary, 0.15);
                color: $primary;
            }
        }
    }

    .search-modal-overlay {
        background: rgba(0, 0, 0, 0.7);
    }

    .search-modal {
        background: $dark;
    }

    .search-modal-footer {
        border-color: $grey-darker;
    }

    .type-filters {
        border-color: $grey-darker;
    }

    .type-filter-trigger {
        background: $grey-darker;
        border-color: $grey-dark;
        color: $grey-light;

        &:hover {
            border-color: $grey;
            color: $white;
        }
    }

    .type-filter-dropdown-panel {
        background: $grey-darker;
    }

    .type-filter-dropdown-header {
        color: $white;
    }

    .type-filter-reset {
        color: lighten($primary, 10%);
    }

    // Recent searches dark mode
    .recent-searches {
        .recent-searches-footer {
            border-color: $grey-darker;
        }

        .footer-divider {
            background: $grey-dark;
        }

        .remove-recent-btn {
            color: $grey-light;

            &:hover {
                color: $danger;
            }
        }

        .clear-recent-btn:hover {
            color: $danger;
            background: rgba($danger, 0.15);
        }

        .advanced-search-btn:hover {
            color: $primary;
            background: rgba($primary, 0.15);
        }
    }

    // Clear confirmation dark mode
    .clear-confirmation {
        .confirmation-content p {
            color: $white;
        }

        .btn-cancel {
            background: $grey-darker;
            border-color: $grey-dark;
            color: $grey-light;

            &:hover {
                background: $grey-dark;
            }
        }
    }
}
</style>
